/***************************************************************************************
 * FileName      : MonitoringPanel.tsx
 * Description   : 학습 좌측 패널 - 데이터 모니터링 값 + 제어 장비 상태 표시 (HMI 화면 제외)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.12 좌측 패널 헤더 → "모니터링" 패널 타이틀 + 상태 뱃지 (장비명 3중 중복 해소), 계측 장비 개수 표시 수정
 * Reference     : mockup/learning.html (LEFT PANEL HMI), doc/scjeil-mtpt-app/순천제일대_디지털트윈/02-equipment-twin.md
 ***************************************************************************************/

import React, { useState } from 'react';
// 2026.02.11 : 박은정 : 장비 JSON 데이터 서비스 추가 (모니터링·제어 데이터 로드)
import { EquipmentDataService, DeviceData, MonitoringSection, MonitoringDataRow } from '../services/EquipmentDataService';
// 2026.02.12 : 박은정 : EQUIPMENT_DATA 임포트 제거 (장비명을 좌측 패널에서 더 이상 표시하지 않음)
// 2026.02.12 : 박은정 : 모니터링 데이터 타입 임포트 (순환 의존성 해소를 위해 types로 이동)
import { MonitoringData } from '../types/MonitoringData';
// 2026.05.13 : 박은정 : 시뮬레이션 값 변화 누적 그래프 (좌측 패널 최상단)
import { PlaybackChart, ChartPoint } from './PlaybackChart';

// 2026.02.11 : 박은정 : 장비 상태 타입 정의
type EquipmentStatus = 'running' | 'stopped' | 'warning' | 'alarm';

interface Props {
  isVisible: boolean;
  equipmentId: string | null;
  // 2026.02.11 : 박은정 : 모니터링 데이터 (LearningPanel의 monitoring 카드에서 생성된 값)
  monitoringData?: MonitoringData | null;
  // 2026.05.13 : 박은정 : 시뮬레이션 누적 시계열 (data 있을 때만 차트 렌더)
  chartHistory?: ChartPoint[];
}

// 2026.02.11 : 박은정 : 학습 좌측 패널 컴포넌트 (데이터 모니터링 + 제어 장비 상태)
export const MonitoringPanel: React.FC<Props> = ({
  isVisible,
  equipmentId,
  monitoringData = null,
  chartHistory = [],
}) => {
  // 2026.02.11 : 박은정 : 아코디언 상태 관리 (제어 장비 상태, 데이터 모니터링) - 기본 닫힘 상태
  const [isControlDevicesOpen, setIsControlDevicesOpen] = useState(false);
  const [isMonitoringDataOpen, setIsMonitoringDataOpen] = useState(false);

  if (!isVisible || !equipmentId) return null;

  // 2026.02.12 : 박은정 : EQUIPMENT_DATA 조회 제거 (장비명은 하단 푸터·우측 패널에서 표시)
  const equipmentDataService = EquipmentDataService.getInstance();
  const equipmentData = equipmentDataService.getEquipmentData(equipmentId);
  if (!equipmentData) return null;

  // 2026.02.11 : 박은정 : 제어 장비(Control 카테고리) 목록 조회
  const controlDevices = equipmentDataService.getControlDevices(equipmentId);
  // 2026.02.11 : 박은정 : 모니터링 센서 데이터 조회 (sensorType별 그룹)
  const rawMonitoringSections = equipmentDataService.getMonitoringData(equipmentId);
  
  // 2026.02.11 : 박은정 : monitoringData가 있으면 센서 값 업데이트
  const monitoringSections: MonitoringSection[] = monitoringData?.sensorValues
    ? rawMonitoringSections.map(section => ({
        ...section,
        dataRows: section.dataRows.map(row => ({
          ...row,
          value: monitoringData.sensorValues[row.label] || row.value,
        })),
      }))
    : rawMonitoringSections;

  // 2026.02.11 : 박은정 : 장비 상태 결정 (현재는 기본값, 추후 실시간 데이터 연동)
  const equipmentStatus: EquipmentStatus = 'stopped';

  // 2026.02.11 : 박은정 : 상태 텍스트 결정
  const getStatusText = (status: EquipmentStatus): string => {
    switch (status) {
      case 'running': return '운전중';
      case 'stopped': return '정지';
      case 'warning': return '주의';
      case 'alarm': return '경고';
      default: return '알 수 없음';
    }
  };

  // 2026.02.11 : 박은정 : 제어 장비 제어 타입별 아이콘 표시
  const getControlTypeLabel = (controlType: string): string => {
    switch (controlType) {
      case 'Command': return 'CMD';
      case 'Setpoint': return 'SV';
      default: return controlType;
    }
  };

  // 2026.02.11 : 박은정 : 데이터 타입에 따른 기본 표시값
  const getDefaultValue = (dataType: string): string => {
    switch (dataType) {
      case 'bool': return 'OFF';
      case 'float': return '-';
      case 'int': return '-';
      default: return '-';
    }
  };

  // 2026.02.11 : 박은정 : 제어 장비 단일 항목 렌더링
  const renderControlDevice = (device: DeviceData, idx: number) => {
    // 제어 장비의 상태 결정 (CMD 중 하나라도 ON이면 운전중)
    let deviceStatus: 'running' | 'stopped' = 'stopped';
    if (device.controls && monitoringData?.controlValues) {
      const hasRunningControl = device.controls.some(ctrl => {
        const value = monitoringData.controlValues?.[ctrl.controlId];
        return ctrl.controlType === 'Command' && value === 'ON';
      });
      if (hasRunningControl) {
        deviceStatus = 'running';
      }
    }
    
    return (
      <div className="mp-device" key={idx}>
        <div className="mp-device__header">
          <div className="mp-device__info">
            <span className="mp-device__id">{device.deviceId}</span>
            <span className="mp-device__name">{device.name}</span>
          </div>
          <div className={`mp-device__status mp-device__status--${deviceStatus}`}>
            <span className="mp-device__status-dot"></span>
            <span className="mp-device__status-text">{deviceStatus === 'running' ? '운전중' : '정지'}</span>
          </div>
        </div>
        {device.role && (
          <div className="mp-device__role">{device.role}</div>
        )}
        {/* 2026.02.11 : 박은정 : 제어 포인트 목록 표시 */}
        {device.controls && device.controls.length > 0 && (
          <div className="mp-device__controls">
            {device.controls.map((ctrl, ci) => {
              // monitoringData에서 제어 값 가져오기
              const controlValue = monitoringData?.controlValues?.[ctrl.controlId];
              const displayValue = controlValue !== undefined ? controlValue : getDefaultValue(ctrl.dataType);
              
              return (
                <div className="mp-ctrl" key={ci}>
                  <div className="mp-ctrl__left">
                    <span className="mp-ctrl__type">{getControlTypeLabel(ctrl.controlType)}</span>
                    <span className="mp-ctrl__name">{ctrl.name}</span>
                  </div>
                  <div className="mp-ctrl__right">
                    <span className="mp-ctrl__value">{displayValue}</span>
                    {ctrl.unit && <span className="mp-ctrl__unit">{ctrl.unit}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // 2026.02.11 : 박은정 : 모니터링 데이터 행 렌더링
  const renderDataRow = (row: MonitoringDataRow, idx: number) => (
    <div className="mp-data-row" key={idx}>
      <div className="mp-data-row__left">
        <span className="mp-data-row__indicator mp-data-row__indicator--normal"></span>
        <div className="mp-data-row__labels">
          <span className="mp-data-row__id">{row.label}</span>
          <span className="mp-data-row__desc">{row.desc}</span>
        </div>
      </div>
      <div className="mp-data-row__right">
        <span className="mp-data-row__value">{row.value}</span>
        <span className="mp-data-row__unit">{row.unit}</span>
      </div>
    </div>
  );

  // 2026.02.11 : 박은정 : 모니터링 섹션 렌더링 (sensorType별 그룹)
  const renderMonitoringSection = (section: MonitoringSection, idx: number) => (
    <div className="mp-section" key={idx}>
      <div className="mp-section__title">{section.heading}</div>
      <div className="mp-section__rows">
        {section.dataRows.map((row, ri) => renderDataRow(row, ri))}
      </div>
    </div>
  );

  return (
    <aside className={`monitoring-panel${isVisible ? ' monitoring-panel--visible' : ''}`}>
      {/* 2026.02.12 : 박은정 : 패널 헤더 → "모니터링" 타이틀 + 운전 상태 뱃지 (장비명은 하단 푸터·우측 패널에서 표시) */}
      <div className="monitoring-panel__header">
        <h2 className="monitoring-panel__title">모니터링</h2>
        <div className={`monitoring-panel__status monitoring-panel__status--${equipmentStatus}`}>
          <span className="monitoring-panel__status-dot"></span>
          <span className="monitoring-panel__status-text">{getStatusText(equipmentStatus)}</span>
        </div>
      </div>

      {/* 2026.02.12 : 박은정 : 장비 설명 제거 (우측 학습 패널 개요에서 제공하므로 중복 해소) */}

      {/* 2026.02.11 : 박은정 : 스크롤 가능한 본문 영역 */}
      <div className="monitoring-panel__body">
        {/* 2026.05.13 : 박은정 : 시뮬레이션 값 변화 누적 그래프 (데이터 있을 때만 렌더, 좌측 최상단) */}
        {chartHistory.length > 0 && (
          <PlaybackChart history={chartHistory} />
        )}

        {/* 2026.02.11 : 박은정 : 제어 장비 상태 섹션 - 학습 시나리오와 동일한 아코디언 스타일 */}
        {controlDevices.length > 0 && (
          <div className={`monitoring-panel__card ${isControlDevicesOpen ? 'monitoring-panel__card--active' : ''}`}>
            <div 
              className="monitoring-panel__card-header"
              onClick={() => setIsControlDevicesOpen(!isControlDevicesOpen)}
            >
              <div className="monitoring-panel__card-left">
                <span className={`monitoring-panel__card-number ${isControlDevicesOpen ? 'monitoring-panel__card-number--active' : ''}`}>
                  1
                </span>
                <span className="monitoring-panel__card-title">제어 장비 상태</span>
              </div>
              <div className="monitoring-panel__card-right">
                <span className="monitoring-panel__card-status">{controlDevices.length}개</span>
                <svg
                  className={`monitoring-panel__card-chevron ${isControlDevicesOpen ? 'monitoring-panel__card-chevron--open' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className={`monitoring-panel__card-content ${isControlDevicesOpen ? 'monitoring-panel__card-content--open' : ''}`}>
              <div className="monitoring-panel__card-body">
                <div className="monitoring-panel__devices">
                  {controlDevices.map((device, idx) => renderControlDevice(device, idx))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2026.02.11 : 박은정 : 모니터링 데이터 섹션 - 학습 시나리오와 동일한 아코디언 스타일 */}
        {monitoringSections.length > 0 && (
          <div className={`monitoring-panel__card ${isMonitoringDataOpen ? 'monitoring-panel__card--active' : ''}`}>
            <div 
              className="monitoring-panel__card-header"
              onClick={() => setIsMonitoringDataOpen(!isMonitoringDataOpen)}
            >
              <div className="monitoring-panel__card-left">
                <span className={`monitoring-panel__card-number ${isMonitoringDataOpen ? 'monitoring-panel__card-number--active' : ''}`}>
                  2
                </span>
                <span className="monitoring-panel__card-title">계측 장비</span>
              </div>
              <div className="monitoring-panel__card-right">
                <span className="monitoring-panel__card-status">{monitoringSections.reduce((acc, s) => acc + s.dataRows.length, 0)}개</span>
                <svg
                  className={`monitoring-panel__card-chevron ${isMonitoringDataOpen ? 'monitoring-panel__card-chevron--open' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className={`monitoring-panel__card-content ${isMonitoringDataOpen ? 'monitoring-panel__card-content--open' : ''}`}>
              <div className="monitoring-panel__card-body">
                <div className="monitoring-panel__sections">
                  {monitoringSections.map((section, idx) => renderMonitoringSection(section, idx))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

